import Agilite from 'agilite'
import store from '../../../../store'
import BillingEnums from '../../utils/enums'
import { createLedgerTransaction, updateFollowUpAccountById, getFollowUpAccounts } from './index'
import { logAuditEvent, AuditActions, AuditEntityTypes } from './auditTrail'

const agilite = new Agilite({
  apiServerUrl: process.env.REACT_APP_AGILITE_API_URL,
  apiKey: process.env.REACT_APP_AGILITE_API_KEY
})

/**
 * Allocate a payment across multiple invoices
 * @param {Object} paymentData - Payment details
 * @returns {Promise} Allocation result
 */
export const allocatePayment = (paymentData) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        const { patientId, totalAmount, paymentMethod, reference, allocations, paymentDate = new Date() } = paymentData

        // Validate EFT transactions have a reference
        if (paymentMethod === BillingEnums.paymentMethods.EFT && (!reference || reference.trim().length === 0)) {
          throw new Error('EFT payments must include a reference number')
        }

        // Validate allocations sum equals total amount
        const allocationSum = allocations.reduce((sum, alloc) => sum + alloc.amount, 0)
        if (Math.abs(allocationSum - totalAmount) > 0.01) {
          throw new Error('Allocation amounts must equal total payment amount')
        }

        // Validate medical aid payments are not allocated to private invoices
        if (paymentMethod === 'medical_aid' || paymentData.payer === 'medical_aid') {
          for (const allocation of allocations) {
            if (allocation.accountGroup === 'private') {
              throw new Error(
                `Medical aid payment cannot be allocated to private invoice ${
                  allocation.invoiceNo || allocation.invoiceRef
                }`
              )
            }
          }
        }

        // Create payment record
        const paymentRecord = await agilite.Connectors.execute(
          BillingEnums.profileKeys.PAYMENT_ALLOCATIONS,
          BillingEnums.routeKeys.CREATE,
          {
            data: JSON.stringify({
              patientId,
              totalAmount,
              paymentMethod,
              reference,
              paymentDate,
              allocations,
              status: 'allocated',
              entityRef: store.getState().core.entity._id,
              createdBy: store.getState().auth.agiliteUser?._id,
              createdAt: new Date()
            })
          }
        )

        // Process each allocation
        for (const allocation of allocations) {
          await createLedgerTransaction({
            patientId,
            invoiceNo: allocation.invoiceNo,
            type: BillingEnums.transactionTypes.PAYMENT,
            description: `Payment allocation - ${paymentMethod}`,
            debit: 0,
            credit: allocation.amount,
            paymentMethod,
            reference,
            allocationRef: paymentRecord.data._id,
            status: 'Allocated'
          })
        }

        // Update follow-up account
        await updateAccountBalances(patientId)

        resolve({
          success: true,
          paymentId: paymentRecord.data._id,
          allocations: allocations.length
        })
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Handle unallocated payment
 * @param {Object} paymentData - Unallocated payment details
 * @returns {Promise} Unallocated payment record
 */
export const recordUnallocatedPayment = (paymentData) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        const { amount, paymentMethod, reference, payerName, payerDetails, notes } = paymentData

        // Create unallocated payment record
        const unallocatedPayment = await agilite.Connectors.execute(
          BillingEnums.profileKeys.UNALLOCATED_PAYMENTS,
          BillingEnums.routeKeys.CREATE,
          {
            data: JSON.stringify({
              amount,
              paymentMethod,
              reference,
              payerName,
              payerDetails,
              notes,
              status: 'unallocated',
              receivedDate: new Date(),
              entityRef: store.getState().core.entity._id,
              createdBy: store.getState().auth.agiliteUser?._id,
              createdAt: new Date()
            })
          }
        )

        resolve(unallocatedPayment.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Allocate an unallocated payment to patient accounts
 * @param {string} unallocatedPaymentId - Unallocated payment ID
 * @param {Object} allocationDetails - Allocation details
 * @returns {Promise} Allocation result
 */
export const allocateUnallocatedPayment = (unallocatedPaymentId, allocationDetails) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        // Get unallocated payment details
        const unallocatedPayment = await agilite.Connectors.execute(
          BillingEnums.profileKeys.UNALLOCATED_PAYMENTS,
          BillingEnums.routeKeys.FIND_BY_ID,
          {
            id: unallocatedPaymentId
          }
        )

        if (!unallocatedPayment.data) {
          throw new Error('Unallocated payment not found')
        }

        // Process allocation
        const result = await allocatePayment({
          ...allocationDetails,
          totalAmount: unallocatedPayment.data.amount,
          paymentMethod: unallocatedPayment.data.paymentMethod,
          reference: unallocatedPayment.data.reference
        })

        // Update unallocated payment status
        await agilite.Connectors.execute(BillingEnums.profileKeys.UNALLOCATED_PAYMENTS, BillingEnums.routeKeys.UPDATE, {
          filter: JSON.stringify({ _id: unallocatedPaymentId }),
          data: JSON.stringify({
            status: 'allocated',
            allocatedDate: new Date(),
            allocatedTo: allocationDetails.patientId,
            allocationRef: result.paymentId
          })
        })

        resolve(result)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Auto-allocate payment to oldest invoices
 * @param {Object} paymentData - Payment details
 * @returns {Promise} Auto-allocation result
 */
export const autoAllocatePayment = (paymentData) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        const { patientId, amount } = paymentData

        // Get patient's outstanding invoices ordered by date
        const outstandingInvoices = await agilite.Connectors.execute(
          BillingEnums.profileKeys.PATIENT_LEDGER,
          BillingEnums.routeKeys.AGGREGATE,
          {
            pipeline: JSON.stringify([
              {
                $match: {
                  patientId,
                  type: BillingEnums.transactionTypes.INVOICE,
                  $or: [{ status: { $ne: 'Paid' } }, { balance: { $gt: 0 } }]
                }
              },
              {
                $sort: { date: 1 } // Oldest first
              }
            ])
          }
        )

        const allocations = []
        let remainingAmount = amount

        // Allocate to invoices in order
        for (const invoice of outstandingInvoices.data) {
          if (remainingAmount <= 0) break

          const invoiceBalance = invoice.debit - (invoice.allocated || 0)
          const allocationAmount = Math.min(remainingAmount, invoiceBalance)

          if (allocationAmount > 0) {
            allocations.push({
              invoiceNo: invoice.invoiceNo,
              amount: allocationAmount
            })
            remainingAmount -= allocationAmount
          }
        }

        // If there's remaining amount, it becomes a credit
        if (remainingAmount > 0) {
          allocations.push({
            invoiceNo: 'CREDIT',
            amount: remainingAmount
          })
        }

        // Process allocation
        const result = await allocatePayment({
          ...paymentData,
          allocations
        })

        resolve(result)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Update account balances after payment
 * @param {string} patientId - Patient ID
 * @returns {Promise} Update result
 */
const updateAccountBalances = async (patientId) => {
  try {
    // Get current account
    const accounts = await getFollowUpAccounts({ patientId }, 1, 1)
    if (!accounts || accounts.length === 0) return

    const account = accounts[0]

    // Calculate new balances from ledger
    const balances = await agilite.Connectors.execute(
      BillingEnums.profileKeys.PATIENT_LEDGER,
      BillingEnums.routeKeys.AGGREGATE,
      {
        pipeline: JSON.stringify([
          {
            $match: { patientId }
          },
          // Join with invoices to check private status
          {
            $lookup: {
              from: 'invoices',
              localField: 'invoiceNo',
              foreignField: 'invoiceNo',
              as: 'invoiceDetails'
            }
          },
          {
            $addFields: {
              // Check if invoice is private (accountGroup = 'private' OR goodXStatus = 'Private' OR no goodXTicketNo)
              isPrivateInvoice: {
                $cond: [
                  { $eq: ['$type', BillingEnums.transactionTypes.INVOICE] },
                  {
                    $or: [
                      { $eq: [{ $arrayElemAt: ['$invoiceDetails.accountGroup', 0] }, 'private'] },
                      { $eq: [{ $arrayElemAt: ['$invoiceDetails.goodXStatus', 0] }, 'Private'] },
                      { $eq: [{ $arrayElemAt: ['$invoiceDetails.goodXTicketNo', 0] }, null] },
                      { $eq: [{ $arrayElemAt: ['$invoiceDetails.goodXTicketNo', 0] }, ''] }
                    ]
                  },
                  false
                ]
              }
            }
          },
          {
            $group: {
              _id: null,
              totalDebits: { $sum: '$debit' },
              totalCredits: { $sum: '$credit' },
              patientDebits: {
                $sum: {
                  $cond: [{ $eq: ['$type', BillingEnums.transactionTypes.INVOICE] }, '$patientAmount', 0]
                }
              },
              patientCredits: {
                $sum: {
                  $cond: [{ $eq: ['$type', BillingEnums.transactionTypes.PAYMENT] }, '$credit', 0]
                }
              },
              medicalAidDebits: {
                $sum: {
                  $cond: [
                    {
                      $and: [
                        { $eq: ['$type', BillingEnums.transactionTypes.INVOICE] },
                        { $ne: ['$isPrivateInvoice', true] },
                        { $not: { $in: ['$compensationType', ['COID', 'FEM', 'SAPS', 'RMA']] } }
                      ]
                    },
                    '$medicalAidAmount',
                    0
                  ]
                }
              },
              compensationDebits: {
                $sum: {
                  $cond: [
                    {
                      $and: [
                        { $eq: ['$type', BillingEnums.transactionTypes.INVOICE] },
                        { $in: ['$compensationType', ['COID', 'FEM', 'SAPS', 'RMA']] }
                      ]
                    },
                    '$compensationAmount',
                    0
                  ]
                }
              },
              medicalAidCredits: {
                $sum: {
                  $cond: [{ $eq: ['$type', BillingEnums.transactionTypes.MEDICAL_AID_PAYMENT] }, '$credit', 0]
                }
              },
              compensationCredits: {
                $sum: {
                  $cond: [
                    {
                      $in: [
                        '$type',
                        ['compensation_payment', 'coid_payment', 'fem_payment', 'saps_payment', 'rma_payment']
                      ]
                    },
                    '$credit',
                    0
                  ]
                }
              }
            }
          }
        ])
      }
    )

    if (balances.data && balances.data.length > 0) {
      const balance = balances.data[0]
      const totalOutstanding = balance.totalDebits - balance.totalCredits
      const patientOutstanding = balance.patientDebits - balance.patientCredits
      const medicalAidOutstanding = balance.medicalAidDebits - balance.medicalAidCredits

      // Determine new status
      let status = 'completed'
      if (patientOutstanding > 0 && medicalAidOutstanding > 0) {
        status = 'awaiting_payment_aid'
      } else if (patientOutstanding > 0) {
        status = 'awaiting_payment_patient'
      } else if (medicalAidOutstanding > 0) {
        status = 'awaiting_payment_aid'
      }

      await updateFollowUpAccountById(account._id, {
        outstandingAmount: totalOutstanding,
        patientOutstanding,
        medicalAidOutstanding,
        status,
        creditBalance: totalOutstanding < 0 ? Math.abs(totalOutstanding) : 0
      })
    }
  } catch (error) {
    console.error('Error updating account balances:', error)
  }
}

/**
 * Get unallocated payments
 * @param {Object} filter - Filter criteria
 * @param {number} page - Page number
 * @param {number} limit - Items per page
 * @returns {Promise} Unallocated payments
 */
export const getUnallocatedPayments = (filter = {}, page = 1, limit = 20) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        if (filter.countOnly) {
          const response = await agilite.Connectors.execute(
            BillingEnums.profileKeys.UNALLOCATED_PAYMENTS,
            BillingEnums.routeKeys.COUNT,
            {
              filter: JSON.stringify({
                entityRef: store.getState().core.entity._id,
                status: 'unallocated'
              })
            }
          )
          resolve({ count: response.data || 0 })
          return
        }

        const query = {
          entityRef: store.getState().core.entity._id,
          status: 'unallocated',
          ...filter
        }

        const response = await agilite.Connectors.execute(
          BillingEnums.profileKeys.UNALLOCATED_PAYMENTS,
          BillingEnums.routeKeys.READ,
          {
            filter: JSON.stringify(query),
            page,
            pageLimit: limit
          }
        )

        // Add age calculation
        const data = (response.data || []).map((payment) => ({
          ...payment,
          ageInDays: Math.floor((new Date() - new Date(payment.paymentDate)) / (1000 * 60 * 60 * 24))
        }))

        resolve({ data, total: response.total || 0 })
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Get split payments
 * @param {Object} filter - Filter criteria
 * @param {number} page - Page number
 * @param {number} limit - Items per page
 * @returns {Promise} Split payments
 */
export const getSplitPayments = (filter = {}, page = 1, limit = 20) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        if (filter.countOnly) {
          const response = await agilite.Connectors.execute(
            BillingEnums.profileKeys.PAYMENT_ALLOCATIONS,
            BillingEnums.routeKeys.COUNT,
            {
              filter: JSON.stringify({
                entityRef: store.getState().core.entity._id,
                'allocations.1': { $exists: true } // Has multiple allocations
              })
            }
          )
          resolve({ count: response.data || 0 })
          return
        }

        const query = {
          entityRef: store.getState().core.entity._id,
          'allocations.1': { $exists: true }, // Has multiple allocations
          ...filter
        }

        const response = await agilite.Connectors.execute(
          BillingEnums.profileKeys.PAYMENT_ALLOCATIONS,
          BillingEnums.routeKeys.READ,
          {
            filter: JSON.stringify(query),
            page,
            pageLimit: limit
          }
        )

        resolve({ data: response.data || [], total: response.total || 0 })
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Get outstanding invoices for allocation
 * @param {Object} filter - Filter criteria
 * @returns {Promise} Outstanding invoices
 */
export const getOutstandingInvoices = (filter = {}) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        const query = {
          entityRef: store.getState().core.entity._id,
          outstandingAmount: { $gt: 0 }
        }

        if (filter.patientSearch) {
          query.$or = [
            { patientName: { $regex: filter.patientSearch, $options: 'i' } },
            { fileNo: { $regex: filter.patientSearch, $options: 'i' } }
          ]
        }

        // Filter out private invoices if this is for medical aid payments
        if (filter.paymentType === 'medical_aid') {
          query.accountGroup = { $ne: 'private' }
        }

        const response = await agilite.Connectors.execute('invoices', BillingEnums.routeKeys.READ, {
          filter: JSON.stringify(query),
          pageLimit: 100,
          sort: JSON.stringify({ invoiceDate: 1 }) // FIFO
        })

        resolve(response.data || [])
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Get refunds
 * @param {Object} filter - Filter criteria
 * @param {number} page - Page number
 * @param {number} limit - Items per page
 * @returns {Promise} Refunds
 */
export const getRefunds = (filter = {}, page = 1, limit = 20) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        const query = {
          entityRef: store.getState().core.entity._id,
          ...filter
        }

        const response = await agilite.Connectors.execute(
          BillingEnums.profileKeys.REFUNDS,
          BillingEnums.routeKeys.READ,
          {
            filter: JSON.stringify(query),
            page,
            pageLimit: limit
          }
        )

        resolve({ data: response.data || [], total: response.total || 0 })
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Create refund
 * @param {Object} refundData - Refund details
 * @returns {Promise} Created refund
 */
export const createRefund = (refundData) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        const refund = {
          ...refundData,
          refundNo: `REF-${Date.now()}`,
          status: 'pending_approval',
          entityRef: store.getState().core.entity._id,
          createdBy: store.getState().auth.agiliteUser?._id,
          createdAt: new Date()
        }

        const response = await agilite.Connectors.execute(
          BillingEnums.profileKeys.REFUNDS,
          BillingEnums.routeKeys.CREATE,
          {
            data: JSON.stringify(refund)
          }
        )

        // Create corresponding ledger transaction
        await createLedgerTransaction({
          patientId: refundData.patientId,
          billingId: refundData.billingId,
          date: refundData.date || new Date(),
          type: BillingEnums.transactionTypes.REFUND,
          description: `Refund - ${refundData.reason || refundData.reasonCode}`,
          debit: parseFloat(refundData.amount),
          credit: 0,
          status: 'Pending Approval',
          reference: refund.refundNo,
          paymentMethod: refundData.paymentMethod,
          notes: `Refund created - Status: Pending Approval. Reason: ${refundData.reason || 'N/A'}`
        })

        // Create audit trail entry
        await logAuditEvent(
          AuditActions.CREATE,
          AuditEntityTypes.REFUND,
          response.data._id,
          `Refund created: ${refund.refundNo} for R${refundData.amount}`,
          {
            refundNo: refund.refundNo,
            amount: refundData.amount,
            patientId: refundData.patientId,
            billingId: refundData.billingId, // Link to follow-up account
            reason: refundData.reason || refundData.reasonCode,
            paymentMethod: refundData.paymentMethod
          }
        )

        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Approve refund
 * @param {string} refundId - Refund ID to approve
 * @returns {Promise} Updated refund
 */
export const approveRefund = (refundId) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        const updateData = {
          status: 'approved',
          approvedBy: store.getState().auth.agiliteUser?._id,
          approvedAt: new Date()
        }

        const response = await agilite.Connectors.execute(BillingEnums.profileKeys.REFUNDS, 'find_by_id_and_update', {
          recordId: refundId,
          data: JSON.stringify(updateData)
        })

        // Create ledger entry for the refund
        await createLedgerTransaction({
          type: BillingEnums.transactionTypes.REFUND,
          debit: response.data.amount,
          credit: 0,
          patientId: response.data.patientId,
          billingId: response.data.billingId,
          reference: response.data.refundNo,
          description: `Refund - ${response.data.reason}`,
          paymentMethod: response.data.paymentMethod
        })

        // Create audit trail entry for approval
        await logAuditEvent(
          AuditActions.APPROVE,
          AuditEntityTypes.REFUND,
          refundId,
          `Refund approved: ${response.data.refundNo} for R${response.data.amount}`,
          {
            refundNo: response.data.refundNo,
            amount: response.data.amount,
            patientId: response.data.patientId,
            billingId: response.data.billingId, // Link to follow-up account
            approvedBy: store.getState().auth.agiliteUser?._id,
            approvedAt: new Date()
          }
        )

        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Get patient balance
 * @param {string} patientId - Patient ID
 * @returns {Promise} Patient balance information
 */
export const getPatientBalance = (patientId) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        // First, get patient's billingId
        let billingId = null
        try {
          const patientData = await agilite.Connectors.execute('auth_users', BillingEnums.routeKeys.FIND_BY_ID, {
            id: patientId
          })
          if (patientData?.data?.billingId) {
            billingId = patientData.data.billingId
          }
        } catch (err) {
          console.warn('Could not fetch patient billing ID:', err)
        }

        // If no billingId from patient record, try to get from follow-up account
        if (!billingId) {
          const accounts = await getFollowUpAccounts({ patientId }, 1, 1)
          if (accounts && accounts.length > 0 && accounts[0].billingId) {
            billingId = accounts[0].billingId
          }
        }

        if (!billingId) {
          console.warn('No billingId found for patient:', patientId)
          resolve({
            currentBalance: 0,
            creditBalance: 0,
            lastPaymentDate: null,
            patientOutstanding: 0,
            medicalAidOutstanding: 0
          })
          return
        }

        // Calculate balances from all ledger transactions
        const balanceResponse = await agilite.Connectors.execute(
          BillingEnums.profileKeys.PATIENT_LEDGER,
          BillingEnums.routeKeys.AGGREGATE,
          {
            pipeline: JSON.stringify([
              {
                $match: {
                  billingId: billingId,
                  entityRef: store.getState().core.entity._id
                }
              },
              // Join with invoices to check private status
              {
                $lookup: {
                  from: 'invoices',
                  localField: 'invoiceNo',
                  foreignField: 'invoiceNo',
                  as: 'invoiceDetails'
                }
              },
              {
                $addFields: {
                  // Check if invoice is private (accountGroup = 'private' OR goodXStatus = 'Private' OR no goodXTicketNo)
                  isPrivateInvoice: {
                    $cond: [
                      { $eq: ['$type', BillingEnums.transactionTypes.INVOICE] },
                      {
                        $or: [
                          { $eq: [{ $arrayElemAt: ['$invoiceDetails.accountGroup', 0] }, 'private'] },
                          { $eq: [{ $arrayElemAt: ['$invoiceDetails.goodXStatus', 0] }, 'Private'] },
                          { $eq: [{ $arrayElemAt: ['$invoiceDetails.goodXTicketNo', 0] }, null] },
                          { $eq: [{ $arrayElemAt: ['$invoiceDetails.goodXTicketNo', 0] }, ''] }
                        ]
                      },
                      false
                    ]
                  }
                }
              },
              {
                $group: {
                  _id: null,
                  totalDebits: { $sum: '$debit' },
                  totalCredits: { $sum: '$credit' },
                  patientDebits: {
                    $sum: {
                      $cond: [{ $eq: ['$type', BillingEnums.transactionTypes.INVOICE] }, '$patientAmount', 0]
                    }
                  },
                  patientCredits: {
                    $sum: {
                      $cond: [
                        {
                          $in: ['$type', [BillingEnums.transactionTypes.PAYMENT, BillingEnums.transactionTypes.CREDIT]]
                        },
                        '$credit',
                        0
                      ]
                    }
                  },
                  medicalAidDebits: {
                    $sum: {
                      $cond: [
                        {
                          $and: [
                            { $eq: ['$type', BillingEnums.transactionTypes.INVOICE] },
                            { $ne: ['$isPrivateInvoice', true] }
                          ]
                        },
                        '$medicalAidAmount',
                        0
                      ]
                    }
                  },
                  medicalAidCredits: {
                    $sum: {
                      $cond: [{ $eq: ['$type', BillingEnums.transactionTypes.MEDICAL_AID_PAYMENT] }, '$credit', 0]
                    }
                  },
                  lastPaymentDate: {
                    $max: {
                      $cond: [
                        {
                          $in: [
                            '$type',
                            [BillingEnums.transactionTypes.PAYMENT, BillingEnums.transactionTypes.MEDICAL_AID_PAYMENT]
                          ]
                        },
                        '$date',
                        null
                      ]
                    }
                  }
                }
              }
            ])
          }
        )

        if (balanceResponse.data && balanceResponse.data.length > 0) {
          const balance = balanceResponse.data[0]
          const totalOutstanding = balance.totalDebits - balance.totalCredits
          const patientOutstanding = balance.patientDebits - balance.patientCredits
          const medicalAidOutstanding = balance.medicalAidDebits - balance.medicalAidCredits

          resolve({
            currentBalance: totalOutstanding,
            creditBalance: totalOutstanding < 0 ? Math.abs(totalOutstanding) : 0,
            lastPaymentDate: balance.lastPaymentDate,
            patientOutstanding: Math.max(0, patientOutstanding),
            medicalAidOutstanding: Math.max(0, medicalAidOutstanding)
          })
        } else {
          // No transactions found
          resolve({
            currentBalance: 0,
            creditBalance: 0,
            lastPaymentDate: null,
            patientOutstanding: 0,
            medicalAidOutstanding: 0
          })
        }
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Search patients
 * @param {string} searchTerm - Search term
 * @returns {Promise} Matching patients
 */
export const searchPatients = (searchTerm) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        // Build search conditions
        const searchConditions = [
          { firstName: { $regex: searchTerm, $options: 'i' } },
          { lastName: { $regex: searchTerm, $options: 'i' } },
          { fileNo: { $regex: searchTerm, $options: 'i' } }
        ]

        // Add full name search by combining first and last name
        searchConditions.push({
          $expr: {
            $regexMatch: {
              input: { $concat: ['$firstName', ' ', '$lastName'] },
              regex: searchTerm,
              options: 'i'
            }
          }
        })

        // Only search billingId if searchTerm is numeric
        const numericSearchTerm = parseInt(searchTerm)
        if (!isNaN(numericSearchTerm)) {
          searchConditions.push({ billingId: numericSearchTerm })
        }

        const response = await agilite.Connectors.execute('auth_users', BillingEnums.routeKeys.FIND, {
          filter: JSON.stringify({
            $and: [
              {
                $or: searchConditions
              }
            ]
          }),
          pageLimit: 20
        })

        const patients = (response.data || []).map((user) => ({
          _id: user._id,
          name: `${user.firstName} ${user.lastName}`,
          billingId: user.billingId,
          fileNo: user.fileNo
        }))

        resolve(patients)
      } catch (e) {
        reject(e)
      }
    })()
  })
}
